import { FC, Fragment, useMemo, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { CommonStyle } from "Components";
import ControlledForm from "Components/ControlledForm";
import CstmBtn from "Components/CstmBtn";
import ImgPath from "Constant/Imgs";
import {
  PreApprovedOptionType,
  PreApprovedPlanType,
  PreApprovedTemplateType
} from "DataTypes/PatientManagement.type";
import InputValidation from "Utils/SwitchCase/InputValidation";

interface ReusablePAPUIProps {
  handleAddUpdateOption: (
    data: PreApprovedOptionType,
    catType: string
  ) => Promise<boolean>;
  handleDeleteOption: (optId: number | null, catType: string) => void;
  isPlanEditable: boolean;
  planData: PreApprovedPlanType
}

const ReusablePAPUI: FC<ReusablePAPUIProps> = (props) => {
  const {
    handleAddUpdateOption,
    handleDeleteOption,
    isPlanEditable,
    planData
  } = props;
  const [editChanges, setEditChanges] =
    useState<PreApprovedOptionType | null>(null);
  const [editCat, setEditCat] = useState("");
  const editErrObj = InputValidation({
    id: "EditText",
    boxType: "input",
    required: true,
    inputVal: editChanges?.label,
    validatetype: "address"
  });

  const handleClose: () => void = () => {
    setEditChanges(null);
    setEditCat("");
  };

  const RenderTextSec: (
    optData: PreApprovedOptionType,
    catType: string
  ) => JSX.Element = (optData, catType) => {
    if (
      editChanges &&
      editChanges.id == optData.id &&
      editChanges.parentId == optData.parentId &&
      editCat == catType
    ) {
      return (
        <Box width={"100%"}>
          <ControlledForm.InputApi
            id="EditText"
            inputVal={editChanges.label}
            inputChange={(val) => {
              setEditChanges({
                ...editChanges,
                label: typeof val == "string" ? val : ""
              })
            }}
            errorText={editErrObj.errorText}
            error={editErrObj.error}
          />
        </Box>
      )
    } else {
      return (
        <Typography
          variant="body2"
          sx={{
            whiteSpace: "normal",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
          }}
        >
          {optData.label}
        </Typography>
      )
    }
  };

  const RenderButtonSec: (
    optData: PreApprovedOptionType,
    catType: string
  ) => JSX.Element = (optData, catType) => {
    if (
      editChanges &&
      editChanges.id == optData.id &&
      editChanges.parentId == optData.parentId &&
      editCat == catType
    ) {
      return (
        <Fragment>
          <IconButton
            sx={{ ...CommonStyle.sxRoundIconsMainMd, mr: 0.5 }}
            onClick={async () => {
              const res = await handleAddUpdateOption(editChanges, editCat);
              if (res) handleClose();
            }}
            disabled={editErrObj.error}
          >
            <ImgPath.SaveIcon />
          </IconButton>
          <IconButton
            sx={{ ...CommonStyle.sxRoundIconsMainMd, mr: 0.5 }}
            onClick={handleClose}
          >
            <ImgPath.CloseIcon />
          </IconButton>
        </Fragment>
      )
    } else {
      return (
        <Fragment>
          <IconButton
            sx={{ ...CommonStyle.sxRoundIconsMainMd, mr: 0.5 }}
            onClick={() => {
              setEditChanges({
                id: null,
                label: "",
                parentId: optData.id
              })
              setEditCat(catType)
            }}
            title="Add"
          >
            <ImgPath.PlusIcon />
          </IconButton>
          <IconButton
            sx={{ ...CommonStyle.sxRoundIconsMainMd, mr: 0.5 }}
            onClick={() => {
              setEditChanges(optData)
              setEditCat(catType)
            }}
          >
            <ImgPath.EditOutlined />
          </IconButton>
          <IconButton
            sx={{ ...CommonStyle.sxRoundIconsMainMd, mr: 0.5 }}
            onClick={() => handleDeleteOption(optData.id, catType)}
          >
            <ImgPath.DeleteOutline />
          </IconButton>
        </Fragment>
      )
    }
  };

  const RenderOptSec: (
    catEl: PreApprovedTemplateType,
    parentId: number | null
  ) => JSX.Element = (catEl, parentId) => {
    const filArr = catEl.options.filter(
      filEl => filEl.parentId == parentId
    );
    const emptyBox: PreApprovedOptionType = {
      id: null,
      label: "",
      parentId: parentId
    }
    return (
      <Fragment>
        {filArr.map(optEl => {
          const childOptions = catEl.options.filter(
            filEl => filEl.parentId == optEl.id
          )
          const childEmptyBox: PreApprovedOptionType = {
            id: null,
            label: "",
            parentId: optEl.id
          }
          return (
            <Box key={optEl.id} sx={{ ...sxCover }} >
              <Box sx={{ ...sxBox }}>
                {RenderTextSec(optEl, catEl.categoryType)}
                {isPlanEditable &&
                  <Box display={"flex"}>
                    {RenderButtonSec(optEl, catEl.categoryType)}
                  </Box>
                }
              </Box>
              {
                (childOptions.length > 0 ||
                  (
                    editChanges &&
                    editChanges.id == childEmptyBox.id &&
                    editChanges.parentId ==
                    childEmptyBox.parentId &&
                    editCat == catEl.categoryType
                  )
                ) &&
                <Box ml={2} mt={2} mb={2}>
                  {RenderOptSec(
                    catEl,
                    optEl.id
                  )}
                </Box>
              }
            </Box>
          )
        })}
        {
          editChanges &&
          editChanges.id == emptyBox.id &&
          editChanges.parentId == emptyBox.parentId &&
          editCat == catEl.categoryType &&
          <Box sx={{ ...sxCover }} >
            <Box sx={{ ...sxBox }}>
              {RenderTextSec(emptyBox, catEl.categoryType)}
              {isPlanEditable &&
                <Box display={"flex"}>
                  {RenderButtonSec(emptyBox, catEl.categoryType)}
                </Box>
              }
            </Box>
          </Box>
        }
      </Fragment>
    )
  };

  return useMemo(() => {
    return (
      <Fragment>
        {planData?.category.map(catEl => {
          return (
            <Box key={catEl.categoryId} mb={4}>
              <Typography color="black" fontWeight={"bold"}>
                {catEl.categoryType}
              </Typography>
              {RenderOptSec(catEl, null)}
              {isPlanEditable &&
                <CstmBtn
                  label="Add More"
                  onClick={() => {
                    setEditCat(catEl.categoryType);
                    setEditChanges({
                      id: null,
                      label: "",
                      parentId: null
                    });
                  }}
                  sxProps={{ mt: 1 }}
                  disable={editChanges ? true : false}
                />
              }
            </Box>
          )
        })}
      </Fragment>
    );
  }, [isPlanEditable, planData, editChanges]);

};
export default ReusablePAPUI;

const sxBox = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 1,
  backgroundColor: "#fff",
  p: 1,
};

const sxCover = {
  border: "1px solid",
  borderColor: "custom.main",
  borderRadius: "5px",
  backgroundColor: "bg.main"
}